import { FC, useState } from "react";
import { FormattedMessage } from "@/i18n/i18n-client";
import { Price } from "@/components/Price";
import { EmptyState } from "@/components/UtilityComponents/EmptyState";
import { FlexRow, Overlay } from "@/shared/globals";
import { Delete } from "@/assets/Icons/Delete";
import { ShoppingBag } from "@/assets/Icons/ShoppingBag";
import { RightArrow } from "@/assets/Icons/RightArrow";
import { LeftArrow } from "@/assets/Icons/LeftArrow";
import { EmptyCartIcon } from "@/assets/Icons/EmptyCartIcon";
import {
  Container,
  CartHeader,
  FullWidth,
  StyledH3,
  ItemsContainer,
  CheckoutSection,
  MarginBottom,
  StyledSubTotal,
  CheckoutButton,
  StyledH3WithMargin,
  CloseButton,
  MobileClose,
} from "../components/SideCart/styled";
import SideCartSingleItem from "../components/SideCart/SideCartSingleItem";
import { PrimaryButton } from "@/components/Button";
import { SideCartProps } from "../../types";
import { useCartData } from "@/lib/cartData/useCartData";
import { isCartDirty } from "@/shared/utils/error-utils";
import { Link, useRouter } from "@/i18n/i18n-navigation";
import { useStore } from "@/lib/storeData/StoreContext";
import { getPaymentLink } from "@/shared/utils/shinglePageCheckoutFunctions";
import { useAuth } from "@/lib/Authentication/hooks/useAuth";
import { useLocale } from "@/i18n/i18n-client";
import CentralizedModal from "@/components/Centralizedlogin/CentralizedModal";
import { AuthMode } from "@/generated/graphql";
import { checkSession } from "@/lib/Authentication/services/authService";
import { handleLoginUpdate } from "@/lib/Authentication/utils/loginHelpers";
import {
  shouldCheckSession,
  validateAndUpdateUserFromToken,
} from "@/lib/Authentication/utils/tokenHelpers";
import { updateUser } from "@/lib/Authentication/utils";

const SideCart: FC<SideCartProps> = () => {
  const {
    cart: { id: cartId, isLoading, receipt, items, cartErrors, isSideCartOpen },
    setSideCartState,
  } = useCartData();
  const { handle, WuiltPayState, storeId, authMode } = useStore();
  const canNotProceedToCheckout = isCartDirty(cartErrors);
  const { user } = useAuth();
  const token = user?.token;
  const { code: localeCode } = useLocale();
  const { isLoggedIn } = useAuth();
  const locale = useLocale();
  const router = useRouter();
  const [isCentralizedModalOpen, setIsCentralizedModalOpen] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);

  const handleCheckoutClick = async () => {
    // If user is already logged in, proceed directly to checkout
    if (isLoggedIn) {
      setSideCartState(false);
      router.push(
        WuiltPayState?.enabled
          ? getPaymentLink(handle, cartId, token, locale?.code)
          : `/checkout/${cartId}`
      );
      return;
    }

    setIsCheckingSession(true);
    try {
      // Check if token exists and validate/update user context
      if (!shouldCheckSession()) {
        // Token exists, validate it and update user context
        validateAndUpdateUserFromToken();
        setIsCheckingSession(false);
        return;
      }

      // No token found, proceed with checkSession
      const response = await checkSession(storeId);

      if (response?.success) {
        // Update user context with session data
        if (response?.data) {
          handleLoginUpdate({
            loginResponseData: response.data,
            token: response?.token,
            updateUser,
          });
        }
        // User is authenticated, proceed to checkout
        setSideCartState(false);
        router.push(
          WuiltPayState?.enabled
            ? getPaymentLink(handle, cartId, token, locale?.code)
            : `/checkout/${cartId}`
        );
      } else {
        // User is not authenticated, open modal
        setIsCentralizedModalOpen(true);
      }
    } catch {
      // Session check failed, open modal for authentication
      if (authMode === AuthMode.Modern) {
        setIsCentralizedModalOpen(true);
      } else {
        setSideCartState(false);
        router.push(
          WuiltPayState?.enabled
            ? getPaymentLink(handle, cartId, token, locale?.code)
            : `/checkout/${cartId}`
        );
      }
    } finally {
      setIsCheckingSession(false);
    }
  };

  return (
    <>
      {isSideCartOpen && <Overlay onClick={() => setSideCartState(false)} />}
      <Container isOpen={!!isSideCartOpen}>
        {isSideCartOpen && (
          <>
            <MobileClose
              data-test="button-close-cart"
              onClick={() => setSideCartState(false)}
            >
              <LeftArrow rotateOnRTL />
            </MobileClose>
            <CartHeader>
              <FlexRow>
                <ShoppingBag />
                <StyledH3WithMargin>
                  <FormattedMessage
                    defaultMessage="Shopping Cart"
                    id="KFD6en"
                  />
                </StyledH3WithMargin>
              </FlexRow>
              <CloseButton
                data-test="button-close-cart"
                onClick={() => setSideCartState(false)}
              >
                <Delete />
              </CloseButton>
            </CartHeader>
            {!items || items?.length === 0 ? (
              <EmptyState
                title={
                  <FormattedMessage
                    defaultMessage="Your cart is empty"
                    id="TtTPxt"
                  />
                }
                description={
                  <FormattedMessage
                    defaultMessage="Looks like you haven’t added anything to your cart yet"
                    id="jd4m/q"
                  />
                }
                image={<EmptyCartIcon />}
              />
            ) : (
              <>
                <ItemsContainer data-test="cart-items-list">
                  {items?.map((item) => (
                    <SideCartSingleItem
                      key={item?.id}
                      item={item}
                      errors={cartErrors}
                    />
                  ))}
                </ItemsContainer>
                <CheckoutSection>
                  <MarginBottom>
                    <FullWidth>
                      <StyledH3>
                        <FormattedMessage
                          defaultMessage="Subtotal"
                          id="L8seEc"
                        />
                      </StyledH3>
                      <StyledSubTotal data-test="text-sidecart-subtotal">
                        {isLoading ? (
                          <FormattedMessage
                            defaultMessage="Calculating..."
                            id="EXlQBN"
                          />
                        ) : (
                          <Price money={receipt?.total} />
                        )}
                      </StyledSubTotal>
                    </FullWidth>
                  </MarginBottom>
                  <MarginBottom>
                    <CheckoutButton
                      data-test="button-checkout"
                      fullWidth
                      suffixIcon={<RightArrow />}
                      onClick={handleCheckoutClick}
                      loadOnRouteChange
                      disabled={isLoading || canNotProceedToCheckout}
                    >
                      <FormattedMessage defaultMessage="Checkout" id="BJ2TKX" />
                    </CheckoutButton>
                  </MarginBottom>
                  <MarginBottom>
                    <Link href="/cart">
                      <PrimaryButton
                        data-test="button-view-cart"
                        fullWidth
                        onClick={() => setSideCartState(false)}
                        loadOnRouteChange
                        outlined
                      >
                        <FormattedMessage
                          defaultMessage="View Cart"
                          id="5BTYa0"
                        />
                      </PrimaryButton>
                    </Link>
                  </MarginBottom>
                </CheckoutSection>
              </>
            )}
          </>
        )}
        {authMode === AuthMode.Modern && (
          <CentralizedModal
            isOpen={isCentralizedModalOpen}
            onClose={() => setIsCentralizedModalOpen(false)}
            onComplete={() => setIsCentralizedModalOpen(false)}
            onAuthSuccess={() => {
              // After successful authentication, navigate to checkout
              setIsCentralizedModalOpen(false);
              setSideCartState(false);
              router.push(
                WuiltPayState?.enabled
                  ? getPaymentLink(handle, cartId, token, locale?.code)
                  : `/checkout/${cartId}`
              );
            }}
          />
        )}
      </Container>
    </>
  );
};

export default SideCart;
